import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";

import { geospatialConvert, useGetVersionQuery } from "fond/api";
import { LayerGroupIds, LayerId, LayerIds } from "fond/layers";
import { getCurrentProjectUploads } from "fond/project";
import { POLYGON_UPLOAD_LAYER_ID } from "fond/project/polygon";
import { reset, retryDownload, selectDrawNewOption, togglePolygonLayer, uploadPolygonComplete } from "fond/project/polygon/redux";
import { dismissUploadError, getCurrentProject, hasLayer, polygonSelectUpload, uploadBegin } from "fond/project/redux";
import { UploadSources } from "fond/project/upload";
import { ProjectState, UploadType, Version } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

export type PolygonSelect = {
  polygon: ProjectState["polygon"];
  activeLayerGroupId: string;
  project: ReturnType<typeof getCurrentProject>;
  version: Version | undefined;
  /** Progress of the polygon file upload, if any. */
  upload: UploadType | undefined;
  isIncludeUgPathDisabled: boolean;
  convertToDualSided: boolean;
  setConvertToDualSided: Dispatch<SetStateAction<boolean>>;
  selectedFiles: File[] | null;
  setSelectedFiles: Dispatch<SetStateAction<File[] | null>>;
  isConfirmingReset: boolean;
  setIsConfirmingReset: Dispatch<SetStateAction<boolean>>;
  onReset(): void;
  onRetryDownload(): void;
  importSelectedArea(): void;
  toggleLayer(layerId: LayerId): void;
  uploadPolygon(): void;
  dismissPolygonUploadError(): void;
};

/** Return the polygon select state and actions for drawing, uploading and importing a selected area. */
export const usePolygonSelect = (): PolygonSelect => {
  const dispatch = useAppDispatch();
  const polygon = useAppSelector((state) => state.project.polygon);
  const { activeLayerGroupId, versionId } = useAppSelector((state) => state.project);
  const project = useAppSelector((state) => getCurrentProject(state.project));
  const uploads = useAppSelector((state) => getCurrentProjectUploads(state.project));
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const projectData = useAppSelector((state) => state.project.projects[state.project.projectId]?.data);

  const [convertToDualSided, setConvertToDualSided] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);

  const upload = uploads?.[POLYGON_UPLOAD_LAYER_ID];

  /**
   * The user is only allowed to include UG path importing if they have not
   * already imported AER data using area select.
   */
  const isIncludeUgPathDisabled = useMemo(() => {
    const hasSpanPoleData = hasLayer(projectData, LayerIds.inSpan) || hasLayer(projectData, LayerIds.inPole);
    const spanPoleDataSource = projectData?.layers[LayerIds.inSpan]?.sources ?? projectData?.layers[LayerIds.inPole]?.sources;

    return (
      activeLayerGroupId === LayerGroupIds.inAddress &&
      hasSpanPoleData &&
      spanPoleDataSource &&
      spanPoleDataSource.includes(UploadSources.polygonSelect)
    );
  }, [activeLayerGroupId, projectData]);

  const onReset = useCallback(() => {
    dispatch(reset(version));
    dispatch(selectDrawNewOption());
  }, [dispatch, version]);

  const onRetryDownload = useCallback(() => dispatch(retryDownload()), [dispatch]);

  const importSelectedArea = useCallback(() => dispatch(polygonSelectUpload(convertToDualSided)), [convertToDualSided, dispatch]);

  const toggleLayer = useCallback((layerId: LayerId) => dispatch(togglePolygonLayer(layerId)), [dispatch]);

  const uploadPolygon = useCallback(() => {
    if (!project) return;
    dispatch(
      uploadBegin(
        project.ID,
        POLYGON_UPLOAD_LAYER_ID,
        ({ xhrEvents }: any) => geospatialConvert({ selectedFiles: { polygon: selectedFiles }, xhrEvents }),
        (responseText: string) => {
          return (dispatch1: any) => {
            const geojson = JSON.parse(responseText);
            dispatch1(uploadPolygonComplete(geojson.Features));
          };
        }
      )
    );
  }, [dispatch, project, selectedFiles]);

  const dismissPolygonUploadError = useCallback(() => {
    setSelectedFiles(null);
    dispatch(dismissUploadError(POLYGON_UPLOAD_LAYER_ID));
  }, [dispatch]);

  return {
    polygon,
    activeLayerGroupId,
    project,
    version,
    upload,
    isIncludeUgPathDisabled: !!isIncludeUgPathDisabled,
    convertToDualSided,
    setConvertToDualSided,
    selectedFiles,
    setSelectedFiles,
    isConfirmingReset,
    setIsConfirmingReset,
    onReset,
    onRetryDownload,
    importSelectedArea,
    toggleLayer,
    uploadPolygon,
    dismissPolygonUploadError,
  };
};
